import { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import {
  FileText,
  Plus,
  Trash2,
  Loader2,
  CheckCircle,
  AlertCircle,
  Upload,
  ExternalLink,
} from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Combobox } from '@/components/ui/combobox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

const documentSchema = z.object({
  documentType: z.enum(['PASSPORT', 'VISA', 'ID_CARD', 'DRIVING_LICENSE', 'OTHER']),
  documentNumber: z.string().min(1),
  issuingCountry: z.string().min(1),
  issueDate: z.string().optional(),
  expiryDate: z.string().optional(),
  fileUrl: z.string().optional(),
});

type DocumentFormData = z.infer<typeof documentSchema>;

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];

interface DocumentsCardProps {
  customerId: string;
  canEdit?: boolean;
}

export function DocumentsCard({ customerId, canEdit = false }: DocumentsCardProps) {
  const { t } = useTranslation(['customers', 'common']);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [deleteDocumentId, setDeleteDocumentId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const documentTypes = [
    { value: 'PASSPORT', label: t('documents.types.passport', 'Pasaport') },
    { value: 'VISA', label: t('documents.types.visa', 'Vize') },
    { value: 'ID_CARD', label: t('documents.types.idCard', 'Kimlik Kartı') },
    { value: 'DRIVING_LICENSE', label: t('documents.types.drivingLicense', 'Ehliyet') },
    { value: 'OTHER', label: t('documents.types.other', 'Diğer') },
  ];

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<DocumentFormData>({
    defaultValues: {
      documentType: 'PASSPORT',
      documentNumber: '',
      issuingCountry: '',
    },
  });

  const utils = trpc.useUtils();

  const { data: documents, isLoading } = trpc.customer.listDocuments.useQuery(customerId);

  const uploadMutation = trpc.upload.uploadFile.useMutation();

  const addDocumentMutation = trpc.customer.addDocument.useMutation({
    onSuccess: () => {
      utils.customer.listDocuments.invalidate(customerId);
      utils.customer.byId.invalidate(customerId);
      setIsAddDialogOpen(false);
      reset();
      setUploadedFileName(null);
      setIsSubmitting(false);
      showSuccessToast(t('documents.addSuccess', 'Döküman eklendi'));
    },
    onError: (error) => {
      setIsSubmitting(false);
      showErrorToast(error.message || t('documents.addError', 'Döküman eklenemedi'));
    },
  });

  const deleteDocumentMutation = trpc.customer.deleteDocument.useMutation({
    onSuccess: () => {
      utils.customer.listDocuments.invalidate(customerId);
      utils.customer.byId.invalidate(customerId);
      setDeleteDocumentId(null);
      showSuccessToast(t('documents.deleteSuccess', 'Döküman silindi'));
    },
    onError: (error) => {
      showErrorToast(error.message || t('documents.deleteError', 'Döküman silinemedi'));
    },
  });

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      showErrorToast(t('documents.fileTooLarge', 'Dosya 10MB\'dan büyük olamaz'));
      return;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      showErrorToast(t('documents.invalidType', 'Sadece PDF, JPG, PNG ve WebP dosyaları yüklenebilir'));
      return;
    }

    setIsUploading(true);
    try {
      const reader = new FileReader();
      const base64 = await new Promise<string>((resolve, reject) => {
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1] || result); // Remove data:xxx;base64, prefix
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const result = await uploadMutation.mutateAsync({
        filename: file.name,
        folder: `customer/${customerId}/documents`,
        contentType: file.type,
        base64Data: base64,
      });

      setValue('fileUrl', result.url);
      setUploadedFileName(file.name);
    } catch {
      showErrorToast(t('documents.uploadError', 'Dosya yüklenemedi'));
    } finally {
      setIsUploading(false);
    }
  };

  const onSubmit = (data: DocumentFormData) => {
    setIsSubmitting(true);
    addDocumentMutation.mutate({
      customerId,
      documentType: data.documentType,
      documentNumber: data.documentNumber,
      issuingCountry: data.issuingCountry,
      issueDate: data.issueDate ? new Date(data.issueDate) : undefined,
      expiryDate: data.expiryDate ? new Date(data.expiryDate) : undefined,
      fileUrl: data.fileUrl || undefined,
    });
  };

  const handleDelete = () => {
    if (deleteDocumentId) {
      deleteDocumentMutation.mutate(deleteDocumentId);
    }
  };

  const getDocumentTypeLabel = (type: string) => {
    return documentTypes.find((dt) => dt.value === type)?.label || type;
  };

  const isExpired = (expiryDate: Date | null) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  const isExpiringSoon = (expiryDate: Date | null) => {
    if (!expiryDate) return false;
    const threeMonthsFromNow = new Date();
    threeMonthsFromNow.setMonth(threeMonthsFromNow.getMonth() + 3);
    return new Date(expiryDate) < threeMonthsFromNow && !isExpired(expiryDate);
  };

  const handleOpenDialog = () => {
    reset();
    setUploadedFileName(null);
    setIsAddDialogOpen(true);
  };

  return (
    <div className="rounded-xl border bg-card p-6">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-semibold flex items-center gap-2">
          <FileText className="h-5 w-5" />
          {t('documents.title', 'Dökümanlar')}
        </h2>
        {canEdit && <Button size="sm" onClick={handleOpenDialog}>
          <Plus className="mr-2 h-4 w-4" />
          {t('documents.add', 'Döküman Ekle')}
        </Button>}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : documents && documents.length > 0 ? (
        <div className="space-y-3">
          {documents.map((doc) => (
            <div
              key={doc.id}
              className="flex items-center justify-between rounded-lg border p-4"
            >
              <div className="flex items-start gap-4">
                <div className="rounded-lg bg-muted p-2">
                  <FileText className="h-5 w-5 text-muted-foreground" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{getDocumentTypeLabel(doc.documentType)}</p>
                    {doc.isVerified && (
                      <Badge variant="outline" className="gap-1 border-green-500 text-green-600">
                        <CheckCircle className="h-3 w-3" />
                        {t('documents.verified', 'Onaylı')}
                      </Badge>
                    )}
                    {isExpired(doc.expiryDate) && (
                      <Badge variant="destructive" className="gap-1">
                        <AlertCircle className="h-3 w-3" />
                        {t('documents.expired', 'Süresi Dolmuş')}
                      </Badge>
                    )}
                    {isExpiringSoon(doc.expiryDate) && (
                      <Badge variant="outline" className="gap-1 border-yellow-500 text-yellow-600">
                        <AlertCircle className="h-3 w-3" />
                        {t('documents.expiringSoon', 'Süresi Yaklaşıyor')}
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {doc.documentNumber} • {doc.issuingCountry}
                  </p>
                  {doc.expiryDate && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('documents.expires', 'Bitiş')}: {format(new Date(doc.expiryDate), 'dd.MM.yyyy')}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {doc.fileUrl && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => window.open(doc.fileUrl!, '_blank')}
                  >
                    <ExternalLink className="mr-1 h-3.5 w-3.5" />
                    {t('documents.viewFile', 'Dosyayı Görüntüle')}
                  </Button>
                )}
                {canEdit && <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setDeleteDocumentId(doc.id)}
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed p-8 text-center">
          <FileText className="mx-auto h-10 w-10 text-muted-foreground/50" />
          <p className="mt-2 text-sm text-muted-foreground">{t('documents.empty', 'Henüz döküman yüklenmemiş')}</p>
          <p className="text-xs text-muted-foreground">
            {t('documents.emptyHint', 'Pasaport, vize veya kimlik dökümanları ekleyin')}
          </p>
        </div>
      )}

      {/* Add Document Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('documents.addTitle', 'Döküman Ekle')}</DialogTitle>
            <DialogDescription>
              {t('documents.addDescription', 'Bu müşteri için yeni bir döküman ekleyin.')}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label>{t('documents.type', 'Döküman Türü')} *</Label>
              <Combobox
                options={documentTypes}
                value={watch('documentType')}
                onValueChange={(value) =>
                  setValue('documentType', value as DocumentFormData['documentType'])
                }
                placeholder={t('documents.selectType', 'Döküman türü seçin')}
                searchPlaceholder={t('common:search', 'Ara...')}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="documentNumber">{t('documents.number', 'Döküman No')} *</Label>
                <Input
                  id="documentNumber"
                  {...register('documentNumber')}
                  placeholder="AB123456"
                />
                {errors.documentNumber && (
                  <p className="text-sm text-red-500">{t('documents.numberRequired', 'Döküman numarası zorunlu')}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="issuingCountry">{t('documents.issuingCountry', 'Düzenleyen Ülke')} *</Label>
                <Input
                  id="issuingCountry"
                  {...register('issuingCountry')}
                  placeholder="TR, US, GB"
                />
                {errors.issuingCountry && (
                  <p className="text-sm text-red-500">{t('documents.countryRequired', 'Ülke bilgisi zorunlu')}</p>
                )}
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="issueDate">{t('documents.issueDate', 'Düzenlenme Tarihi')}</Label>
                <Input
                  type="date"
                  id="issueDate"
                  {...register('issueDate')}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="expiryDate">{t('documents.expiryDate', 'Bitiş Tarihi')}</Label>
                <Input
                  type="date"
                  id="expiryDate"
                  {...register('expiryDate')}
                />
              </div>
            </div>

            {/* File Upload */}
            <div className="space-y-2">
              <Label>{t('documents.file', 'Dosya')}</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                onChange={handleFileSelect}
                className="hidden"
              />
              {uploadedFileName ? (
                <div className="flex items-center gap-2 rounded border border-green-200 bg-green-50 px-3 py-2 text-sm">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span className="text-green-700">{uploadedFileName}</span>
                  <button
                    type="button"
                    onClick={() => {
                      setUploadedFileName(null);
                      setValue('fileUrl', '');
                    }}
                    className="ml-auto text-xs text-muted-foreground hover:text-foreground"
                  >
                    {t('common:remove', 'Kaldır')}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="flex w-full items-center justify-center gap-2 rounded border-2 border-dashed border-border py-4 text-sm text-muted-foreground hover:border-foreground hover:text-foreground transition-colors disabled:opacity-50"
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t('documents.uploading', 'Yükleniyor...')}
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4" />
                      {t('documents.selectFile', 'Dosya seçin (PDF, JPG, PNG - maks 10MB)')}
                    </>
                  )}
                </button>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsAddDialogOpen(false)}
                disabled={isSubmitting}
              >
                {t('common:cancel', 'İptal')}
              </Button>
              <Button type="submit" disabled={isSubmitting || isUploading}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('documents.add', 'Döküman Ekle')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteDocumentId} onOpenChange={() => setDeleteDocumentId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('documents.deleteTitle', 'Dökümanı Sil')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('documents.deleteConfirm', 'Bu dökümanı silmek istediğinize emin misiniz? Bu işlem geri alınamaz.')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:cancel', 'İptal')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteDocumentMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {t('common:delete', 'Sil')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
